/**
 * Invoice Service
 * API calls for invoice management
 */

import api from './api.js';
import type { ApiResponse, PaginatedResponse } from '@/types/api.js';
import type {
  Invoice,
  InvoiceWithItems,
  InvoiceCreateInput,
  InvoiceUpdateInput,
  InvoiceListParams,
  InvoiceStatistics,
  InvoiceStatus,
} from '@/types/invoice.js';

export const invoicesService = {
  /**
   * Get paginated list of invoices
   */
  async list(params: InvoiceListParams = {}): Promise<PaginatedResponse<Invoice>> {
    const response = await api.get<ApiResponse<PaginatedResponse<Invoice>>>('/invoices', {
      params,
    });
    return response.data.data;
  },

  /**
   * Get invoice by ID
   */
  async getById(id: string): Promise<InvoiceWithItems> {
    const response = await api.get<ApiResponse<InvoiceWithItems>>(`/invoices/${id}`);
    return response.data.data;
  },

  /**
   * Create new invoice
   */
  async create(data: InvoiceCreateInput): Promise<InvoiceWithItems> {
    const response = await api.post<ApiResponse<InvoiceWithItems>>('/invoices', data);
    return response.data.data;
  },

  /**
   * Update invoice
   */
  async update(id: string, data: InvoiceUpdateInput): Promise<InvoiceWithItems> {
    const response = await api.put<ApiResponse<InvoiceWithItems>>(`/invoices/${id}`, data);
    return response.data.data;
  },

  /**
   * Delete invoice
   */
  async delete(id: string): Promise<void> {
    await api.delete(`/invoices/${id}`);
  },

  /**
   * Update invoice status
   */
  async updateStatus(id: string, status: InvoiceStatus): Promise<Invoice> {
    const response = await api.patch<ApiResponse<Invoice>>(`/invoices/${id}/status`, {
      status,
    });
    return response.data.data;
  },

  /**
   * Send invoice via email
   */
  async send(id: string, email?: string): Promise<void> {
    await api.post(`/invoices/${id}/send`, { email });
  },

  /**
   * Mark invoice as sent
   */
  async markSent(id: string): Promise<Invoice> {
    const response = await api.post<ApiResponse<Invoice>>(`/invoices/${id}/mark-sent`);
    return response.data.data;
  },

  /**
   * Void invoice
   */
  async void(id: string, reason: string): Promise<Invoice> {
    const response = await api.post<ApiResponse<Invoice>>(`/invoices/${id}/void`, {
      reason,
    });
    return response.data.data;
  },

  /**
   * Get invoice PDF
   */
  async getPdf(id: string): Promise<Blob> {
    const response = await api.get(`/invoices/${id}/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Duplicate invoice
   */
  async duplicate(id: string): Promise<InvoiceWithItems> {
    const response = await api.post<ApiResponse<InvoiceWithItems>>(`/invoices/${id}/duplicate`);
    return response.data.data;
  },

  /**
   * Bulk delete invoices
   */
  async bulkDelete(ids: string[]): Promise<void> {
    await api.post('/invoices/bulk-delete', { ids });
  },

  /**
   * Get invoice statistics
   */
  async getStatistics(): Promise<InvoiceStatistics> {
    const response = await api.get<ApiResponse<InvoiceStatistics>>('/invoices/statistics');
    return response.data.data;
  },

  /**
   * Record payment for invoice
   */
  async recordPayment(
    id: string,
    data: { amount: number; method: string; reference?: string; notes?: string }
  ): Promise<Invoice> {
    const response = await api.post<ApiResponse<Invoice>>(`/invoices/${id}/payments`, data);
    return response.data.data;
  },
};

export default invoicesService;
